import { Close } from "@mui/icons-material";
import {
  Alert,
  Autocomplete,
  Button,
  IconButton,
  Modal,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useGetAllEmployeesQuery } from "src/api/services/employeesApi";
import { useBulkUpdateWaitingRoomsMutation } from "src/api/services/tasksApi";
import { Employee } from "src/api/types";
import { QUEUE_TYPE } from "src/constants/searchParams";
import { useSelection } from "src/context/ItemsSelectionProvider/useSelection";
import { isError } from "src/utils/isError";
import { desktopSx } from "src/utils/styles/modalStyles";

interface Props {
  open: boolean;
  handleClose: () => void;
}

const ReassignRoomModal = ({ open, handleClose }: Props) => {
  const { selected, setSelected } = useSelection();
  const [params] = useSearchParams();

  const { data: clinicians } = useGetAllEmployeesQuery({
    role: params.get(QUEUE_TYPE) ?? undefined,
  });
  const [reassignRooms] = useBulkUpdateWaitingRoomsMutation();
  const [selectedClinician, setSelectedClinician] = useState<Employee | null>(
    null,
  );
  const [error, setError] = useState<string | null>("");
  const [success, setSuccess] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const handleReassignRooms = useCallback(async () => {
    setLoading(true);
    if (selectedClinician == null) {
      setError("Please make sure that a clinician has been selected");
      return;
    }

    try {
      await reassignRooms({
        assignee_id: selectedClinician.id,
        assignee_type: "Provider",
        ids: selected,
        status: "ASSIGNED",
        assigned_at: new Date().toISOString(),
      }).unwrap();

      setSuccess(`Successfully reassigned rooms: ${selected.join(", ")}`);
      setSelectedClinician(null);
      setSelected([]);
    } catch (error) {
      if (isError(error)) {
        setError(error.data.message);
      } else {
        setError("Room reassignment was unsuccessful, please try again");
      }
    } finally {
      setLoading(false);
    }
  }, [reassignRooms, selected, selectedClinician, setSelected]);

  const onCloseModal = useCallback(() => {
    handleClose();
    setSuccess("");
    setError("");
  }, [handleClose]);

  return (
    <Modal open={open}>
      <Stack sx={desktopSx}>
        <Stack gap={2}>
          <Stack
            direction={"row"}
            justifyContent="space-between"
            alignItems="center"
            gap={10}
          >
            <Typography variant="h5" fontWeight={600}>
              Waiting Room Reassignment Center
            </Typography>
            <IconButton onClick={onCloseModal}>
              <Close fontSize="medium" />
            </IconButton>
          </Stack>
          <Alert severity="warning">
            <Typography
              color="warning"
              sx={{ textDecoration: "underline" }}
              variant="h6"
            >
              Rooms selected
            </Typography>
            <Typography fontWeight={600} variant="h6">
              {selected.join(", ")}
            </Typography>
          </Alert>
          <Autocomplete
            options={clinicians?.data ?? []}
            getOptionLabel={(c) =>
              c
                ? `${c.first_name} ${c.last_name} - ${c.roles?.map((r) => r.display_name ?? r.name).join(", ")}`
                : ""
            }
            value={selectedClinician}
            onChange={(_, newValue) => setSelectedClinician(newValue)}
            renderInput={(params) => (
              <TextField {...params} label="Clinician" required />
            )}
            isOptionEqualToValue={(o, v) => o.id === v.id}
          />
          {error ? (
            <Alert severity="error" variant="filled">
              {error}
            </Alert>
          ) : success ? (
            <Alert severity="success" variant="filled">
              {success}
            </Alert>
          ) : null}
          <Button
            onClick={handleReassignRooms}
            loading={loading}
            disabled={!selectedClinician}
          >
            Reassign
          </Button>
        </Stack>
      </Stack>
    </Modal>
  );
};

export default ReassignRoomModal;
